import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';

import Responsive from '~/common/components/Responsive';

import { isIOSDevice } from '~/common/utils/deviceDetection.js';
import { BREAKPOINT_MEDIUM, BREAKPOINT_MEDIUM_LARGE } from '~/common/utils/consts.js';

import styles from './tooltip.css';

// Tooltip specific consts
const CONTENT_ARROW_WIDTH = 7;
const CONTENT_MAX_WIDTH = 248; // measured in px, [= (.content max-width)]
const WRAPPER_PADDING = 12; // measured in px, [= (.wrapper horiz. padding)]
const WRAPPER_MAX_WIDTH = CONTENT_MAX_WIDTH + WRAPPER_PADDING; // measured in px, [= (.content max-width) + (.wrapper edge padding)]
const IS_IOS = isIOSDevice(); // device type never changes during runtime, only calculate once

@inject('responsiveStore')
@observer
export default class Tooltip extends Component {
    // using visibility instead of display to allow measurement of content dimensions
    state = {
        isVisible: false, // is tooltip content visible
        isFocused: false, // is trigger in focus
        fromDirection: 'left', // which side of tooltip content is facing trigger
        prevInnerWidth: null, // to prevent unnecessary calculations from hover/focus-induced renders
        positionAttrs: {
            left: `calc(100% - ${WRAPPER_PADDING}px)`,
            top: '50%'
        },
        tooltipArrowPositionForTopDisplay: {
            left: 'calc(50% - 7px)'
        }
    };

    tooltipWrapperElement = null;
    tooltipTriggerElement = null;

    // iOS-specific nonsense
    handleIOSClick = (ev) => {
        if (!this.tooltipWrapperElement.contains(ev.target) && this.state.isVisible) {
            if (this.tooltipTriggerElement.contains(ev.target)) {
                this.tooltipTriggerElement.focus();
            } else this.tooltipTriggerElement.blur();
        }
    };

    setTooltipWrapperElement(elem) {
        this.tooltipWrapperElement = elem;
    }

    setTooltipTriggerElement(elem) {
        this.tooltipTriggerElement = elem;
    }

    componentDidMount() {
        if (IS_IOS) {
            if (document.body.style.cursor !== 'pointer') document.body.style.cursor = 'pointer';
            document.addEventListener('click', this.handleIOSClick);
        }
        this.setState(this.calculateTooltipDirection());
    }

    componentWillUnmount() {
        if (IS_IOS) {
            document.removeEventListener('click', this.handleIOSClick);
        }
    }

    componentDidUpdate() {
        const { prevInnerWidth, isVisible, fromDirection } = this.state;
        const { responsiveStore, isVisibleOnErrorOnly, hasError } = this.props;
        // don't recalculate anything if innerWidth hasn't changed
        if (prevInnerWidth === responsiveStore.innerWidth) {
            // hide tooltip if error is gone and tooltip is still visible

            if (isVisibleOnErrorOnly && !hasError && isVisible) {
                this.setState({ isVisible: false });
            } else return;
        }


        // calculate new direction and position for tooltip
        const nextDirectionAndPosition = this.calculateTooltipDirection();

        // new direction needed, update everything
        if (fromDirection !== nextDirectionAndPosition.fromDirection) {
            this.setState(nextDirectionAndPosition);
        } else {
            // same direction calculated, only update the current window size
            this.setState({ prevInnerWidth: nextDirectionAndPosition.prevInnerWidth });
        }
    }

    showTooltip() {
        if (!this.state.isVisible) this.setState({ isVisible: true });
    }

    hideTooltip() {
        if (!this.state.isFocused && this.state.isVisible) this.setState({ isVisible: false });
    }

    handleTriggerFocus() {
        // don't show error tooltip unless there's an error
        if (this.props.isVisibleOnErrorOnly && !this.props.hasError) return;

        this.setState({ isFocused: true, isVisible: true });
    }

    handleTriggerBlur() {
        this.setState({ isFocused: false, isVisible: false });
    }

    calculateTooltipPosition(fromDirection) {
        if (!this.tooltipWrapperElement) return null;

        const triggerBounds = this.tooltipTriggerElement.getClientRects();

        const tooltipWrapperBounds = this.tooltipWrapperElement.getClientRects();

        // Set the left position of tooltip if it is set to display Top
        // Checks to fit the tooltip content within the bounds of window
        let tooltipLeftPositionForTopDisplay = 0;
        if (triggerBounds[0].x > (((50 / 100) * tooltipWrapperBounds[0].width) + (CONTENT_MAX_WIDTH / 2))) {
            tooltipLeftPositionForTopDisplay = `calc(50% - ${(CONTENT_MAX_WIDTH / 2) + 60}px)`;
        } else {
            tooltipLeftPositionForTopDisplay = 0;
        }


        // If tooltip display is top and tooltip content is positioned on `left:0`,
        // we need to check and position the tooltip arrow to not to go outside the trigger element
        if (tooltipLeftPositionForTopDisplay === 0) {
            if ((((50 / 100) * tooltipWrapperBounds[0].width) - 7) > (triggerBounds[0].width + triggerBounds[0].x)) {
                this.setState({
                    tooltipArrowPositionForTopDisplay: {
                        left: `${triggerBounds[0].width - triggerBounds[0].x}px`
                    }
                });
            }
        }

        return fromDirection === 'top' ?
            // tooltip comes from the top of the trigger
            {
                left: tooltipLeftPositionForTopDisplay,
                bottom: `calc(100% - ${WRAPPER_PADDING - CONTENT_ARROW_WIDTH}px)`
            }
            :
            // tooltip comes from left or right side of the trigger bounding box
            {
                [fromDirection]: `calc(100% - ${WRAPPER_PADDING - CONTENT_ARROW_WIDTH}px)`,
                top: this.tooltipWrapperElement ?
                    `calc(50% - ${this.tooltipWrapperElement.getClientRects()[0].height / 2}px)`
                    :
                    0
            };
    }

    calculateTooltipDirection() {
        const { responsiveStore, fromDirection, tooltipPositionTouch } = this.props;
        // if mobile viewport
        if ((responsiveStore.innerWidth < BREAKPOINT_MEDIUM.value) || (responsiveStore.innerWidth <= BREAKPOINT_MEDIUM_LARGE.value && tooltipPositionTouch === 'top')) {
            return {
                prevInnerWidth: responsiveStore.innerWidth,
                fromDirection: 'top',
                positionAttrs: this.calculateTooltipPosition('top') || {}
            };
        }

        // using getClientRects for Edge and IE11 support
        const triggerBoundingBox = this.tooltipTriggerElement.getClientRects()[0];

        const finalFromDirection = (responsiveStore.innerWidth - triggerBoundingBox.right) > WRAPPER_MAX_WIDTH ?
            // tooltip will come from trigger's right side
            'left'
            :
            triggerBoundingBox.left > WRAPPER_MAX_WIDTH ?
                // no space on trigger's right side, tooltip will come from left
                'right'
                :
                // no space on either horiz. side, tooltip will come from top
                'top';

        return {
            prevInnerWidth: responsiveStore.innerWidth,
            fromDirection: finalFromDirection || fromDirection,
            positionAttrs: this.calculateTooltipPosition(finalFromDirection || fromDirection) || {}
        };
    }

    renderTooltipWrapper() {
        const { isVisible, fromDirection, positionAttrs } = this.state;
        const { content, contentModifiers, hasError } = this.props;
        return (
            <Responsive trackingSize={true}>
                <div
                    className={`${
                        styles.wrapper} ${
                        fromDirection ? styles[`wrapper--${fromDirection}`] : ''} ${
                        isVisible ? styles['wrapper--visible'] : ''
                    }`}
                    ref={elem => this.setTooltipWrapperElement(elem)}
                    style={positionAttrs}
                >
                    <div
                        className={`${
                            styles.content} ${
                            hasError ? styles['content--error'] : ''} ${
                            contentModifiers || ''
                        }`}
                        id="tooltip"
                        onMouseDown={ev => ev.preventDefault()}
                    >
                        <span
                            className={`${styles['tooltip-arrow']} ${fromDirection ? styles[`tooltip-arrow--${fromDirection}`] : ''} ${hasError ? styles['tooltip-arrow--error'] : ''}`}
                            style={fromDirection === 'top' ? this.state.tooltipArrowPositionForTopDisplay : {}}
                        />
                        {content}
                    </div>
                </div>
            </Responsive>
        );
    }

    render() {
        const { isVisible } = this.state;
        // child elements to render
        const { children, isInlineTrigger, isVisibleOnFocusOnly, triggerModifiers, activeTriggerModifiers } = this.props;

        return (
            <div
                className={`${
                    styles.trigger} ${
                    isInlineTrigger ? styles['trigger--inline'] : ''} ${
                    triggerModifiers || ''} ${
                    (isVisible && activeTriggerModifiers) ? activeTriggerModifiers : ''
                }`}
                onBlur={() => this.handleTriggerBlur()}
                onFocus={() => this.handleTriggerFocus()}
                onMouseEnter={isVisibleOnFocusOnly ? () => {} : () => this.showTooltip()}
                onMouseLeave={isVisibleOnFocusOnly ? () => {} : () => this.hideTooltip()}
                ref={elem => this.setTooltipTriggerElement(elem)}
                tabIndex={0}
            >
                {children}
                {this.renderTooltipWrapper()}
            </div>
        );
    }
}

Tooltip.propTypes = {
    contentModifiers: PropTypes.string,
    triggerModifiers: PropTypes.string,
    activeTriggerModifiers: PropTypes.string,
    children: PropTypes.node.isRequired,
    content: PropTypes.node.isRequired,
    isInlineTrigger: PropTypes.bool,
    isVisibleOnFocusOnly: PropTypes.bool,
    isVisibleOnErrorOnly: PropTypes.bool,
    hasError: PropTypes.bool,
    componentModifiers: PropTypes.string
};

Tooltip.defaultProps = {
    isInlineTrigger: true,
    isVisibleOnFocusOnly: false,
    isVisibleOnErrorOnly: false,
    hasError: false
};
